import BalloonPlayground from '../components/BalloonPlayground';
import React from 'react';
import { Box, Typography, useMediaQuery, useTheme, } from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import backgroundImage from '../assets/images/background.png';
import styled, { keyframes } from 'styled-components';
import markerIcon from '../assets/map_marker/regular-marker.png';

// 首页背景图片透明度：0 = 完全透明，1 = 完全不透明。
const HOME_BACKGROUND_OPACITY = 0.5;


const floatUp = keyframes`
  0% { transform: translateY(calc(100vh + 160px)); }
  100% { transform: translateY(calc(-80vh)); }
`;

const wobble = keyframes`
  0% { transform: translateX(0px); }
  25% { transform: translateX(6px); }
  50% { transform: translateX(0px); }
  75% { transform: translateX(-6px); }
  100% { transform: translateX(0px); }
`;

const BalloonsLayer = styled('div')`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 80vh !important;
  pointer-events: none;
  z-index: 10;
`;

const FloatingBalloon = styled(Box)`
  position: absolute;
  left: ${props => `${props.startx}px`};
  bottom: -160px;
  width: ${props => `${props.width}px`};
  height: ${props => `${Math.round(props.height)}px`};
  animation: ${floatUp} ${props => props.duration}s linear infinite;
  animation-delay: ${props => props.delay}s;
  will-change: transform;
  text-decoration: none;
  color: inherit;
  &:hover, &:focus-within { animation-play-state: paused; }
  &:hover > *, &:focus-within > * { animation-play-state: paused; }
  &:focus-visible { outline: 2px solid #a50030; outline-offset: 4px; border-radius: 8px; }
`;

const BalloonRow = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  height: 100%;
  animation: ${wobble} ${props => props.wobbleduration}s ease-in-out infinite;
`;

const BalloonPic = styled('img')`
  width: 64px;
  height: 92px;
  object-fit: contain;
  filter: drop-shadow(0 6px 10px rgba(0,0,0,0.18));
`;

const TextBox = styled(Box)`
  border-radius: 10px;
  padding: 8px 10px;
  color: #000;
  max-width: 210px;
`;

const TitleText = styled(Typography)`
  font-weight: 800 !important;
  font-size: 0.95rem !important;
  line-height: 1.2 !important;
  margin-bottom: 4px !important;
`;

const StoryText = styled(Typography)`
  font-size: 0.6rem !important;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

function StartupPage({ supabase }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // fetch recent stories for balloons
  const [stories, setStories] = React.useState([]);
  React.useEffect(() => {
    const fetchStories = async () => {
      try {
        if (!supabase) return;
        const limit = isMobile ? 8 : 12;
        const { data, error } = await supabase
          .from('submissions')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(limit);
        if (error) throw error;
        setStories(data || []);
      } catch (e) {
        console.error('加载故事失败:', e);
      }
    };
    fetchStories();
  }, [supabase, isMobile]);

  const [balloons, setBalloons] = React.useState([]);
  React.useEffect(() => {
    const createBalloons = () => {
      const vw = Math.max(window.innerWidth, 360);
      const count = Math.min(stories.length || (isMobile ? 8 : 12), isMobile ? 8 : 12);
      const minW = isMobile ? 180 : 200;
      const maxW = isMobile ? 300 : 320;
      const heightBase = isMobile ? 110 : 120;
      const intervals = [];
      const margin = 28;
      const safePadding = 20;
      const items = [];
      for (let i = 0; i < count; i++) {
        const w = Math.round(minW + Math.random() * (maxW - minW));
        const minX = safePadding;
        const maxX = Math.max(minX, vw - w - safePadding);
        const intersects = (x, width) => intervals.some(({ s, e }) => !(x + width + margin <= s || x >= e + margin));
        let x = 0;
        let placed = false;
        for (let t = 0; t < 400; t++) {
          x = Math.floor(minX + Math.random() * (maxX - minX));
          if (!intersects(x, w)) {
            intervals.push({ s: x, e: x + w });
            placed = true;
            break;
          }
        }
        if (!placed) {
          const span = (maxX - minX) / Math.max(count, 1);
          x = Math.floor(minX + i * span + span / 2 - w / 2);
          x = Math.max(minX, Math.min(x, maxX));
          intervals.push({ s: x, e: x + w });
        }
        const duration = 32 + Math.random() * 20;
        const delay = -Math.random() * duration;
        const wobbleduration = 6 + Math.random() * 6;
        items.push({ startX: x, width: w, height: heightBase, duration, wobbleduration, delay });
      }
      setBalloons(items);
    };
    createBalloons();
    window.addEventListener('resize', createBalloons);
    return () => window.removeEventListener('resize', createBalloons);
  }, [isMobile, stories.length]);

  const menuItems = [
    { label: '写故事', path: '/map' },
    { label: '故事档案', path: '/archive' },
    { label: '行动', path: '/action' },
    { label: '工具箱', path: '/resources' },
    { label: '关于我们', path: '/about' }
  ];

  const gradientHeight = isMobile ? '20vh' : '35vh';

  return (
    <BalloonPlayground><Box sx={{ minHeight: '100vh', width: '100vw', position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: gradientHeight, background: 'linear-gradient(to bottom, rgba(255, 0, 94, 1) 0%, transparent 100%)', zIndex: 2 }} />

      <BalloonsLayer>
        {balloons.map((b, idx) => {
          const s = stories[idx % Math.max(stories.length, 1)] || {};
          const title = s.here_happened;
          const text = (s.description||'').slice(0, 110);
          return (
            <FloatingBalloon data-floating-balloon component={s.id != null ? Link : "div"} to={s.id != null ? `/incident?id=${encodeURIComponent(s.id)}` : undefined} aria-label={s.id != null ? `阅读故事：${title || s.id}` : undefined} style={{ pointerEvents: s.id != null ? "auto" : "none" }} key={idx} startx={b.startX} width={b.width} height={b.height} duration={b.duration} delay={b.delay}>
              <BalloonRow wobbleduration={b.wobbleduration}>
                <BalloonPic src={markerIcon} alt="balloon" />
                <TextBox>
                  <TitleText variant="subtitle2">{title}</TitleText>
                  <StoryText>{text}</StoryText>
                </TextBox>
              </BalloonRow>
            </FloatingBalloon>
          );
        })}
      </BalloonsLayer>

      {/* Background image */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', opacity: HOME_BACKGROUND_OPACITY, zIndex: 1 }} />

  {/* Title above balloons */}
  <Box sx={{ position: 'absolute', top: { xs: '72px', sm: '50px' }, left: { xs: '20px', sm: '3.5vw' }, maxWidth: 'calc(100% - 40px)', zIndex: 20, textAlign: 'left', pointerEvents: 'none' }}>
        <Typography component="h1" sx={{ fontFamily: 'balloon',fontSize: { xs: '28px', sm: '60px' }, fontWeight: '500', color: '#000', textTransform: 'uppercase', lineHeight: 1.1, textAlign: 'left' }}>
          Archive of Sino<br />
          Women in Diaspora
        </Typography>
      </Box>

  <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 20, width: '100%', padding: 3, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', gap: { xs: 3, md: 6 } }}>
        {menuItems.map((item, index) => (
          <Typography data-no-balloon key={index} onClick={() => navigate(item.path)} sx={{ fontSize: { xs: '1rem', md: '1.2rem' }, color: '#000', cursor: 'pointer', fontFamily: 'SimHei, sans-serif', fontWeight: location.pathname === item.path ? 'bold' : 'normal', transition: 'font-weight 0.2s ease', '&:hover': { fontWeight: 'bold' } }}>
            {item.label}
          </Typography>
        ))}
      </Box>
    </Box></BalloonPlayground>
  );
}

export default StartupPage;
